// onboarding.{brand} primary route. Restores Q&A by transactionId, opens the
// B2C HUB_SIGNUP iframe in a native <dialog>, hands off to online.{brand} on
// success. Mirrors the Hono worker's routes/onboarding.tsx behaviour.

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { HubModalDialog } from "./lib/HubModalDialog";
import { DepotProvisioning } from "./lib/DepotProvisioning";
import { authClient } from "./lib/auth";
import { ensureModalStylesheet } from "./lib/modal";
import { b2cHost, brandLabel, resolveBrand, surfaceUrl, type Brand } from "./lib/brand";

interface OnboardingPayload {
  transactionId: string;
  brand: Brand;
  qa: Record<string, string>;
  createdAt: string;
}

async function fetchOnboarding(transactionId: string): Promise<OnboardingPayload> {
  const res = await fetch(
    `/api/mf-auth-adapter/v0/onboarding/${encodeURIComponent(transactionId)}`,
    { credentials: "same-origin" },
  );
  if (res.status === 404) throw new Error("not_found");
  if (res.status === 410) throw new Error("expired");
  if (!res.ok) throw new Error(`onboarding_load_failed_${res.status}`);
  return (await res.json()) as OnboardingPayload;
}

function readTransactionId(): string | null {
  const params = new URLSearchParams(window.location.search);
  // Accept the new `transactionId` param plus the legacy `uuid` for tabs
  // still on the old worker URL shape — same logic as the worker route.
  return params.get("transactionId") ?? params.get("uuid");
}

/**
 * RFC 4648 §5 base64url, no padding. Required by the adapter for `return=`
 * because Spring Cloud Gateway's strict URI validator rejects raw `=` in
 * query values (and percent-encoded `%3D` decodes back to one before
 * validation). base64url's alphabet has zero of {`+`, `/`, `=`}, so it
 * survives the gateway untouched. The adapter's
 * AuthController.decodeReturnUri base64url-decodes it on receipt.
 *
 * Lives in the SPA now because the Worker is a dumb proxy in the PoC
 * (and there is no Worker at all in the prod target — SPAs go to AFD
 * directly). UTF-8 first so non-ASCII in the return URL round-trips
 * cleanly through java.util.Base64.getUrlDecoder() + UTF-8 on the
 * adapter side.
 */
function base64UrlEncode(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export interface AppProps {
  // Test seam: lets the unit test inject a transactionId without touching
  // window.location. Production flow always reads from the URL.
  transactionIdOverride?: string | null;
  brandOverride?: Brand;
}

export function App({ transactionIdOverride, brandOverride }: AppProps = {}) {
  useEffect(() => {
    ensureModalStylesheet();
  }, []);

  const brand = brandOverride ?? resolveBrand();
  const transactionId =
    transactionIdOverride !== undefined ? transactionIdOverride : readTransactionId();

  // The funnel entry to return to (the AVD product home). `start` is retired —
  // identity lives at `id`, products funnel in at altersvorsorge.{root}.
  const homeUrl = useMemo(() => surfaceUrl("altersvorsorge", brand), [brand]);

  // Detect "signed in" by calling GET /auth/me on mount. A 200 → claims
  // (state 2: depot continuation); 401 → null (state 1: signup). The query
  // is cheap and re-runs when the user returns here post-signup with the new
  // __Host-mf_session cookie set by the adapter's callback.
  const me = useQuery({
    queryKey: ["onboarding", "me"],
    queryFn: () => authClient.me(),
    retry: false,
    staleTime: 0,
  });

  if (me.isLoading) {
    return (
      <main className="hub-page-header">
        <h1>Checking your session…</h1>
      </main>
    );
  }

  // State 2 — already signed in: skip signup, offer depot creation.
  if (me.data) {
    return <DepotProvisioning session={me.data} brand={brand} />;
  }

  // State 1 — not signed in.
  if (!transactionId) {
    return (
      <main className="hub-page-header">
        <h1>Open the link from your email</h1>
        <p>
          We couldn&apos;t find a transaction id on this page. Onboarding starts
          on the <a href={homeUrl}>{brandLabel(brand)} get-started page</a> —
          open the link we sent to your inbox to continue.
        </p>
      </main>
    );
  }

  return (
    <ContentForTransaction
      transactionId={transactionId}
      brand={brand}
      homeUrl={homeUrl}
    />
  );
}

interface ContentProps {
  transactionId: string;
  brand: Brand;
  homeUrl: string;
}

// Which B2C flow the modal runs. `signup` creates a new account (carries the
// onboarding transactionId so the questionnaire is claimed); `signin`
// authenticates an existing user. `null` = modal closed.
type ModalFlow = "signup" | "signin";

function ContentForTransaction(props: ContentProps) {
  const { transactionId, brand, homeUrl } = props;
  const [modalFlow, setModalFlow] = useState<ModalFlow | null>(null);

  const onboarding = useQuery({
    queryKey: ["onboarding", transactionId],
    queryFn: () => fetchOnboarding(transactionId),
    retry: false,
  });

  // BFF-routed: the iframe loads `GET /api/mf-auth-adapter/auth/login?...`
  // which the adapter 302s to the B2C SIGNUP policy with PKCE wired up.
  // After signup completes, B2C → adapter callback → 302 to `return`. The
  // adapter sets the new __Host-mf_session cookie on that callback response.
  //
  // Explicit-onboarding flow: return to THIS onboarding surface (not online).
  // On return, App's /auth/me query now sees a session → state 2 renders the
  // "Create your depot" continuation. The user explicitly provisions and only
  // then hands off to online.{brand}.
  // The transactionId travels as a query param on the `return` URI so this
  // surface keeps it across the round-trip (the signed-in state doesn't need
  // it today, but preserving it keeps the URL stable and correlatable).
  // surfaceUrl("onboarding", ...) resolves the same root the user came in on
  // (canonical tenant slug, brand alias, or legacy).
  const onboardingReturnUri = `${surfaceUrl("onboarding", brand)}/?transactionId=${encodeURIComponent(transactionId)}`;
  // `uuid` carries the onboarding transactionId through to B2C as an
  // OAuth2 KV param. The B2C signup policy reads {OAUTH-KV:uuid} and
  // forwards it to the adapter's /v1/signup so the IAM service can
  // correlate the new account back to the start-page questionnaire.
  //
  // `return` is base64url-encoded — NOT percent-encoded — see
  // base64UrlEncode docs above for the Spring-Cloud-Gateway reason.
  const signupSrc = `/api/mf-auth-adapter/auth/login?brand=${brand}&flow=signup&uuid=${encodeURIComponent(transactionId)}&return=${base64UrlEncode(onboardingReturnUri)}`;
  // Existing-user sign-in: runs the B2C SIGNIN policy instead of SIGNUP. No
  // `uuid` — an existing account doesn't re-claim the anonymous questionnaire.
  // It returns straight to online.{brand} (the dashboard), since a returning
  // user already has an account + depot and doesn't need the depot-provisioning
  // continuation. The adapter callback sets mf_session and 302s the modal iframe
  // to online.{brand}, where id-online's iframe-breakout lifts the top frame.
  const onlineReturnUri = `${surfaceUrl("online", brand)}/`;
  const signinSrc = `/api/mf-auth-adapter/auth/login?brand=${brand}&flow=signin&return=${base64UrlEncode(onlineReturnUri)}`;
  const iframeSrc = modalFlow === "signin" ? signinSrc : signupSrc;

  if (onboarding.isLoading) {
    return (
      <main className="hub-page-header">
        <h1>Loading your details…</h1>
      </main>
    );
  }

  if (onboarding.isError) {
    const reason =
      onboarding.error instanceof Error ? onboarding.error.message : "load_failed";
    const expired = reason === "expired" || reason === "not_found";
    return (
      <main className="hub-page-header">
        <h1>Onboarding session {expired ? "expired" : "unavailable"}</h1>
        <p>
          {expired
            ? "Your transaction has expired or is unknown. Start over from the public site to enter your details again."
            : `We couldn’t reach the adapter (${reason}). Try again in a moment.`}
        </p>
        <p>
          <a href={homeUrl} className="hub-cta-button secondary">
            Start over
          </a>
        </p>
      </main>
    );
  }

  const data = onboarding.data!;
  return (
    <>
      <header className="hub-page-header">
        <h1>Create your {brandLabel(brand)} account</h1>
        <p>
          You&apos;re about to sign up. The next step opens the hosted Azure AD
          B2C sign-up form running our custom policy. Email verification happens
          inside that form: you enter your address, type the 6-digit code we
          email you, and pick a password. When the policy completes you&apos;ll
          land on your {brandLabel(brand)} dashboard already signed in.
        </p>
      </header>
      <section className="hub-explainer" data-testid="qa-recap">
        <h2>What we already know</h2>
        <ul className="hub-qa-list">
          {Object.entries(data.qa).map(([k, v]) => (
            <li key={k}>
              <strong>{k}:</strong> {v}
            </li>
          ))}
        </ul>
      </section>
      <main className="hub-cta-row">
        <button
          type="button"
          className="hub-cta-button"
          onClick={() => setModalFlow("signup")}
        >
          Continue to sign-up
        </button>
        <button
          type="button"
          className="hub-cta-button secondary"
          data-hub-signin
          onClick={() => setModalFlow("signin")}
        >
          Sign in
        </button>
        <a href={homeUrl} className="hub-cta-button secondary">
          Start over
        </a>
      </main>
      <p className="muted" style={{ margin: "10px 0 0", fontSize: 13 }}>
        Already have a {brandLabel(brand)} account? <strong>Sign in</strong>{" "}
        instead — we&apos;ll take you straight to your dashboard.
      </p>
      <HubModalDialog
        b2cHost={b2cHost(brand)}
        iframeSrc={iframeSrc}
        iframeTitle={modalFlow === "signin" ? "Sign in" : "Account setup"}
        open={modalFlow !== null}
        onClose={() => setModalFlow(null)}
      />
    </>
  );
}
